import React, { useState, useEffect } from 'react';
import { View, BackHandler, StyleSheet } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectPantrySnapshot, selectTidyCount } from '../../../redux/pantrySelectors';
import { addItemsAndSync, removeItemAndSync, markImportReviewedAndSync } from '../../../redux/inventorySlice';
import { PantryLocation } from '../../../utils/pantryLocation';
import { consumePendingPantryToast } from '../pendingToast';
import { usePantryToast, PantryToast } from '../PantryToast';
import { usePantryActions } from '../usePantryActions';
import { useClearPantry } from '../useClearPantry';
import { trackEvent } from '../../../services/analyticsService';
import { ItemSheet } from './ItemSheet';
import { MoveSheet } from './MoveSheet';
import { AddWhereSheet } from './AddWhereSheet';
import { QuickAddSheet } from './QuickAddSheet';
import { ReviewSheet } from './ReviewSheet';
import { DedupeSheet } from './DedupeSheet';
import { HomeView } from './HomeView';
import { DeliveryReview } from '../DeliveryReview';
import { useImportLink } from '../useImportLink';
import { ZoneView } from './ZoneView';
import { ConfirmationModal } from '../../common/ConfirmationModal';
import { EmptyState } from '../EmptyState';
import { PantryImportBanner } from '../PantryImportBanner';
import { activeImportBatch } from '../importBatch';
import { CookView } from './CookView';
import { RecipesSheet } from './RecipesSheet';
import { useScanGate } from '../../auth/ScanSignUp';
import { Paprika } from '../../../styles/theme';
import { detectCategoryFromName } from '../../../utils/inventoryUtils';
import { estimateExpiry } from '../../../services/pantryFreshnessService';
import { buildDemoItems } from '../demoSeed';
import { RootState, AppDispatch } from '../../../redux/store';
import type { PantrySheet } from '../types';

export function PantryMobile() {
  const router = useRouter();
  const snapshot = useSelector(selectPantrySnapshot);
  const tidyCount = useSelector(selectTidyCount);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const [screen, setScreen] = useState<'home' | 'zone' | 'cook' | 'import'>('home');
  const [zone, setZone] = useState<PantryLocation>('fridge');
  // Held in state rather than derived: confirming the review marks the batch
  // reviewed, so activeImportBatch goes null while the review is still up.
  const [openImportId, setOpenImportId] = useState<string | null>(null);
  const [sheet, setSheet] = useState<PantrySheet | null>(null);
  const { toast, flash, clear } = usePantryToast();
  const actions = usePantryActions(flash);
  const clearPantry = useClearPantry(flash);
  // A scan needs an account (#775): a guest gets the sign-up sheet, not the camera.
  const scanGate = useScanGate('pantry', '/inventory');
  const openCapture = (params: { analysisMode: 'receipt' } | { analysisMode: 'fridge'; zone: PantryLocation }) =>
    scanGate.requireAccount(params.analysisMode, () =>
      router.push({ pathname: '/(app)/inventory-camera', params }),
    );

  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (sheet) {
        setSheet(null);
        return true;
      }
      if (screen !== 'home') {
        setScreen('home');
        return true;
      }
      return false;
    });
    return () => sub.remove();
  }, [screen, sheet]);

  // Consume pending toast on screen focus (e.g., after returning from recognition-results)
  useFocusEffect(() => {
    const pendingMsg = consumePendingPantryToast();
    if (pendingMsg) {
      flash(pendingMsg);
    }
  });

  const hasDemoItems = snapshot.all.some((v) => v.item.source === 'demo');

  const importBatch = React.useMemo(
    () => activeImportBatch(snapshot.all.map((v) => v.item)),
    [snapshot.all]
  );

  // The basket is reviewed on the receipt scan's screen. Closing it without
  // confirming leaves the banner up; only the confirm marks it reviewed.
  const handleOpenImport = React.useCallback((importId: string, source: 'banner' | 'email' = 'banner') => {
    trackEvent('pantry_import_opened', { importId, source });
    setSheet(null);
    setOpenImportId(importId);
    setScreen('import');
  }, []);

  useImportLink(React.useCallback((importId: string) => handleOpenImport(importId, 'email'), [handleOpenImport]));

  const handleDismissImport = (importId: string) => {
    trackEvent('pantry_import_dismissed', { importId });
    dispatch(markImportReviewedAndSync({ importId, userId }));
  };

  const handleLoadDemoSeed = async () => {
    const demoItems = buildDemoItems();
    dispatch(addItemsAndSync({ items: demoItems as any, userId }));
    trackEvent('pantry_demo_seeded');
    flash(t('pantry.add.addedCount', { count: demoItems.length }));
  };

  const handleRemoveDemoItems = async () => {
    const demoItems = snapshot.all.filter((v) => v.item.source === 'demo');
    for (const view of demoItems) {
      dispatch(removeItemAndSync({ itemId: view.item.id, userId }));
    }
    flash(t('pantry.empty.demoRemove'));
  };

  return (
    <View style={styles.root}>
      {screen === 'home' && snapshot.totalCount === 0 ? (
        <EmptyState
          onReceipt={() => {
            openCapture({ analysisMode: 'receipt' });
          }}
          onPhotoZone={(z) => {
            openCapture({ analysisMode: 'fridge', zone: z });
          }}
          onManual={(z) => {
            setZone(z);
            setSheet({ t: 'quickadd', zone: z });
          }}
          onSeed={handleLoadDemoSeed}
        />
      ) : screen === 'home' ? (
        <>
        {importBatch && (
          <PantryImportBanner
            batch={importBatch}
            onPress={() => handleOpenImport(importBatch.importId)}
            onDismiss={() => handleDismissImport(importBatch.importId)}
          />
        )}
        <HomeView
          snapshot={snapshot}
          hasDemoItems={hasDemoItems}
          onRemoveDemoItems={handleRemoveDemoItems}
          onOpenZone={(z) => {
            trackEvent('pantry_zone_opened', { zone: z });
            setZone(z);
            setScreen('zone');
          }}
          onOpenCook={() => {
            trackEvent('pantry_cook_list_opened');
            setScreen('cook');
          }}
          onOpenReview={() => setSheet({ t: 'review' })}
          duplicateCount={tidyCount}
          onOpenDedupe={() => setSheet({ t: 'dedupe' })}
          onAddWhere={() => setSheet({ t: 'addwhere' })}
          onReceipt={() => {
            openCapture({ analysisMode: 'receipt' });
          }}
          onClearPantry={clearPantry.ask}
          clearBusy={clearPantry.busy}
        />
        </>
      ) : null}
      {screen === 'import' && !!openImportId && (
        <DeliveryReview
          key={openImportId}
          importId={openImportId}
          onClose={() => setScreen('home')}
          onDone={(message) => {
            setScreen('home');
            flash(message);
          }}
        />
      )}
      {screen === 'zone' && (
        <ZoneView
          zone={zone}
          snapshot={snapshot}
          onBack={() => setScreen('home')}
          onItemPress={(id) => setSheet({ t: 'item', itemId: id })}
          onPhoto={() => {
            openCapture({ analysisMode: 'fridge', zone: zone });
          }}
          onQuickAdd={() => setSheet({ t: 'quickadd', zone })}
        />
      )}
      {screen === 'cook' && (
        <CookView
          items={snapshot.cookList}
          onBack={() => setScreen('home')}
          onItemPress={(itemId) => {
            setSheet({ t: 'recipes', itemId });
          }}
          onEat={(item) => {
            actions.markEaten(item.item);
          }}
        />
      )}

      {/* Task 7: Item & Move sheets */}
      {sheet?.t === 'item' && (
        <ItemSheet
          visible={true}
          onClose={() => setSheet(null)}
          item={snapshot.all.find((v) => v.item.id === sheet.itemId)?.item || null}
          actions={actions}
          onMove={() => setSheet({ t: 'move', itemId: sheet.itemId })}
        />
      )}
      {sheet?.t === 'move' && (
        <MoveSheet
          visible={true}
          onClose={() => setSheet(null)}
          item={snapshot.all.find((v) => v.item.id === sheet.itemId)?.item || null}
          actions={actions}
        />
      )}

      {/* Task 8: AddWhere and QuickAdd sheets */}
      {sheet?.t === 'addwhere' && (
        <AddWhereSheet
          visible={true}
          onClose={() => setSheet(null)}
          onZone={(selectedZone) => {
            setZone(selectedZone);
            setSheet({ t: 'quickadd', zone: selectedZone });
          }}
          onReceipt={() => {
            openCapture({ analysisMode: 'receipt' });
          }}
        />
      )}
      {sheet?.t === 'quickadd' && (
        <QuickAddSheet
          visible={true}
          onClose={() => setSheet(null)}
          zone={sheet.zone}
          onAdd={(food) => {
            const category = detectCategoryFromName(food.name);
            const expiryDate = estimateExpiry(category, sheet.zone, new Date(), food.name);
            const items = [
              {
                name: food.name,
                category,
                confidence: 100,
                quantity: 1,
                location: sheet.zone,
                ...(expiryDate && { expiry_date: expiryDate }),
              },
            ];
            dispatch(addItemsAndSync({ items: items as any, userId }));
            const zoneName = t(`pantry.zones.${sheet.zone}`);
            flash(t('pantry.add.addedToZone', { name: food.name, zone: zoneName }));
          }}
          onPhoto={() => {
            openCapture({ analysisMode: 'fridge', zone: sheet.zone });
          }}
        />
      )}

      {/* Task 13: Recipes sheet */}
      {sheet?.t === 'recipes' && (
        <RecipesSheet
          visible={true}
          onClose={() => setSheet(null)}
          itemId={sheet.itemId}
          item={snapshot.all.find((v) => v.item.id === sheet.itemId)?.item || null}
        />
      )}

      {/* Task 14: Review sheet */}
      {sheet?.t === 'review' && (
        <ReviewSheet
          visible={true}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet?.t === 'dedupe' && (
        <DedupeSheet
          visible={true}
          onClose={() => setSheet(null)}
        />
      )}

      <ConfirmationModal
        visible={clearPantry.confirming}
        title={t('pantry.clear.confirmTitle')}
        message={t('pantry.clear.confirmBody', { count: snapshot.totalCount })}
        confirmLabel={t('pantry.clear.confirmAction')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={clearPantry.confirm}
        onCancel={clearPantry.cancel}
      />

      {/* sheet rendering → Tasks 13, 14 */}
      {scanGate.sheet}
      {toast && (
        <PantryToast
          toast={toast}
          onUndo={() => {
            toast.undo?.();
            clear();
          }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
});
